import type { PpaAvailabilityRule } from 'utils/ppaHelpers';

/**
 * Adapts the backend's unified config tree (`GET /ppa/meta/performance-summary`)
 * into the legacy-shaped `{ title, features: [{ feature_id, tiles | options.tabs }] }`
 * structure that `PerformanceSummary.tsx`, `OverallPerformanceSection.tsx` and
 * `PerformanceByLensSection.tsx` already know how to render.
 *
 * Kept as a single translation boundary so those components (and their tests)
 * don't need to know the tree ever existed — if the backend's node shape
 * changes, only this file should need to change.
 */

type UnifiedDefaultSort = {
    control_id: string;
    direction: 'asc' | 'desc';
};

type UnifiedInputValidation = {
    min?: number | null;
    max?: number | null;
    step?: number;
};

type UnifiedKpiMeta = {
    kpi_name: string;
    display_label: string;
    display_format?: string | null;
    decimal_places?: number | null;
    value_prefix?: string;
    value_suffix?: string;
    show_trend?: boolean;
    found?: boolean;
};

type UnifiedNodeProps = {
    label?: string;
    is_default_selected?: boolean;
    dimension?: string;
    is_attribute?: boolean;
    available_in?: PpaAvailabilityRule[];
    default_sort?: UnifiedDefaultSort;
    value_type?: string;
    default_value?: string | number | null;
    placeholder?: string;
    validation?: UnifiedInputValidation;
    input_id?: string;
    [key: string]: unknown;
};

export type UnifiedMetaNode = {
    id: string;
    type: string;
    required: boolean;
    constraints?: Record<string, unknown>;
    props: UnifiedNodeProps;
    kpi_meta?: UnifiedKpiMeta | null;
    objects: UnifiedMetaNode[];
};

export type AdaptedInputConfig = {
    input_id: string;
    input_type?: string;
    default_value?: string | number | null;
    placeholder?: string;
    increment_rate?: number;
    bounds?: { min_val?: number; max_val?: number };
};

export type AdaptedOption = {
    id: string;
    label: string;
    dimension?: string;
    is_attribute?: boolean;
    is_default?: boolean;
    is_mandatory?: boolean;
    input_list?: AdaptedInputConfig;
    /** KPI-only: empty/absent = valid regardless of the selected dimensions. */
    available_in?: PpaAvailabilityRule[];
};

export type AdaptedTab = {
    id: string;
    label: string;
    is_default?: boolean;
    dimension?: string;
    group_by_options?: AdaptedOption[];
    column_options?: AdaptedOption[];
    default_sort?: UnifiedDefaultSort;
};

export type AdaptedTile = {
    kpi_id: string;
    title: string;
    show: boolean;
    showTrend: boolean;
    valuePrefix: string;
    valueSuffix: string;
};

export type AdaptedFeature = {
    feature_id: string;
    tiles?: AdaptedTile[];
    options?: { tabs: AdaptedTab[] };
};

export type AdaptedPerformanceSummaryMeta = {
    title: string;
    features: AdaptedFeature[];
};

const DIMENSION_NODE_TYPES = new Set(['attribute', 'identifier', 'period']);

const adaptTile = (node: UnifiedMetaNode): AdaptedTile => {
    const meta = node.kpi_meta;
    return {
        kpi_id: node.id,
        title: meta?.display_label ?? node.props?.label ?? node.id,
        show: true,
        showTrend: Boolean(meta?.show_trend),
        valuePrefix: meta?.value_prefix ?? '',
        valueSuffix: meta?.value_suffix ?? '',
    };
};

/**
 * The editable input's type/bounds/placeholder live on a nested
 * `input_box`-type child (see `performance_summary.json`'s
 * `migrate_price_price_change_pct`/`migrate_boost_price_change_pct`), not
 * on the `kpi` node's own props — `props.input_id` on the `kpi` node is
 * just the binding reference to it.
 */
const adaptInputList = (node: UnifiedMetaNode): AdaptedInputConfig | undefined => {
    const inputId = node.props?.input_id;
    if (!inputId) return undefined;

    const inputBox = (node.objects ?? []).find((child) => child.type === 'input_box');
    const { value_type: inputType, default_value: defaultValue, placeholder, validation } = inputBox?.props ?? {};
    const hasBounds = validation != null && (validation.min != null || validation.max != null);

    return {
        input_id: inputId as string,
        input_type: inputType as string | undefined,
        default_value: (defaultValue as string | number | null | undefined) ?? null,
        placeholder: placeholder as string | undefined,
        increment_rate: validation?.step,
        bounds: hasBounds
            ? {
                  min_val: validation.min ?? undefined,
                  max_val: validation.max ?? undefined,
              }
            : undefined,
    };
};

/** `attribute` nodes join an attribute table by default; `identifier`/`period` nodes never do. */
const resolveIsAttribute = (node: UnifiedMetaNode): boolean | undefined => {
    if (node.type === 'period') return undefined;
    return (node.props?.is_attribute as boolean | undefined) ?? node.type === 'attribute';
};

const adaptDimensionOption = (node: UnifiedMetaNode): AdaptedOption => ({
    id: node.id,
    label: (node.props?.label as string | undefined) ?? node.id,
    dimension: node.props?.dimension as string | undefined,
    is_attribute: resolveIsAttribute(node),
    is_default: Boolean(node.props?.is_default_selected),
    is_mandatory: Boolean(node.required),
});

const adaptKpiOption = (node: UnifiedMetaNode): AdaptedOption => ({
    id: node.id,
    label: (node.props?.label as string | undefined) ?? node.kpi_meta?.display_label ?? node.id,
    is_default: Boolean(node.props?.is_default_selected),
    is_mandatory: Boolean(node.required),
    input_list: adaptInputList(node),
    available_in: node.props?.available_in,
});

const adaptTab = (tabNode: UnifiedMetaNode): AdaptedTab => {
    const groupByOptions: AdaptedOption[] = [];
    const columnOptions: AdaptedOption[] = [];

    (tabNode.objects ?? []).forEach((child) => {
        if (DIMENSION_NODE_TYPES.has(child.type)) {
            groupByOptions.push(adaptDimensionOption(child));
        } else if (child.type === 'kpi') {
            columnOptions.push(adaptKpiOption(child));
        }
    });

    return {
        id: tabNode.id,
        label: (tabNode.props?.label as string | undefined) ?? tabNode.id,
        is_default: Boolean(tabNode.props?.is_default_selected),
        dimension: groupByOptions[0]?.dimension,
        group_by_options: groupByOptions,
        column_options: columnOptions,
        default_sort: tabNode.props?.default_sort,
    };
};

const adaptFeature = (topNode: UnifiedMetaNode): AdaptedFeature => {
    if (topNode.type === 'tiles_group') {
        return {
            feature_id: topNode.id,
            tiles: (topNode.objects ?? []).map(adaptTile),
        };
    }
    return {
        feature_id: topNode.id,
        options: { tabs: (topNode.objects ?? []).map(adaptTab) },
    };
};

export const adaptPerformanceSummaryMeta = (root: UnifiedMetaNode): AdaptedPerformanceSummaryMeta => ({
    title: (root.props?.label as string | undefined) ?? '',
    features: (root.objects ?? []).map(adaptFeature),
});
